import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../lib/api';

const AuthContext = createContext(null);
const TOKEN_KEY = 'numerica_token';
const USER_KEY = 'numerica_user';

function readStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_err) {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [user, setUser] = useState(() => readStoredUser());
  const [loading, setLoading] = useState(false);

  const persist = useCallback((nextToken, nextUser) => {
    setToken(nextToken);
    setUser(nextUser);
    if (nextToken) localStorage.setItem(TOKEN_KEY, nextToken);
    else localStorage.removeItem(TOKEN_KEY);
    if (nextUser) localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    else localStorage.removeItem(USER_KEY);
  }, []);

  const startGuest = useCallback(async (username) => {
    const payload = await apiRequest('/auth/guest', {
      method: 'POST',
      body: { username }
    });
    persist(payload.token, payload.user);
    return payload.user;
  }, [persist]);

  const login = useCallback(async (emailOrUsername, password) => {
    const payload = await apiRequest('/auth/login', {
      method: 'POST',
      body: { emailOrUsername, password }
    });
    persist(payload.token, payload.user);
    return payload.user;
  }, [persist]);

  const register = useCallback(async ({ email, username, password }) => {
    const payload = await apiRequest('/auth/register', {
      method: 'POST',
      body: { email, username, password }
    });
    persist(payload.token, payload.user);
    return payload.user;
  }, [persist]);

  const logout = useCallback(() => {
    persist('', null);
  }, [persist]);

  useEffect(() => {
    async function validateSession() {
      if (!token || !user) return;
      if (user.type === 'guest') return;
      setLoading(true);
      try {
        const payload = await apiRequest('/auth/me', { token });
        persist(token, payload.user);
      } catch (_err) {
        logout();
      } finally {
        setLoading(false);
      }
    }
    validateSession();
  }, [token, user, logout, persist]);

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      isAuthenticated: Boolean(user),
      isGuest: user?.type === 'guest',
      startGuest,
      login,
      register,
      logout
    }),
    [token, user, loading, startGuest, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
